<script setup lang="ts">
  import { Button } from '@/components/ui/button'
  import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
  import { inject, ref, computed, type Ref, watch, onMounted, onBeforeUnmount } from 'vue'
  import {
    Paperclip,
    Mic,
    SendHorizontal,
    Loader2,
    Bookmark,
    FilePlus2,
    CheckCircle,
    Info,
    Upload
  } from 'lucide-vue-next'
  import type { AIChatSession, CustomerCase } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import PromptTemplatesPopover from './PromptTemplatesPopover.vue'
  import { ROUTES } from '@/router/routes'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { emitter } from '@/store/app/case/detail/eventEmitter'

  const { t } = useTranslation({
    en: {
      YourMessage: 'Your message...',
      Generating: 'Generating...',
      SendMessage: 'Send message',
      AttachFile: 'Attach file',
      RecordVoice: 'Record voice message',
      PromptTemplates: 'Prompt templates',
      GenerateDocument: 'Generate document',
      AllDocumentsAnalyzed: 'All documents analyzed',
      DocumentsInAnalysis: '{count} Documents still in analysis',
      DocumentsBeingAnalyzed: 'Documents are being analyzed - AI may produce incorrect results',
      Processing: 'Processing:',
      DocumentsReady: 'Documents ready for AI',
      Uploading: 'Uploading:',
      DataExtraction: 'Data Extraction',
      BasicAIAnalysis: 'Basic AI Analysis',
      Summary: 'Summary',
      AdvancedAIAnalysis: 'Advanced AI Analysis',
      SummaryEmbedding: 'Summary Embedding',
      Analysis: 'Analysis',
      Queued: 'Queued'
    },
    de: {
      YourMessage: 'Ihre Nachricht...',
      Generating: 'Generiere...',
      SendMessage: 'Nachricht senden',
      AttachFile: 'Datei anhängen',
      RecordVoice: 'Sprachnachricht aufnehmen',
      PromptTemplates: 'Promptvorlagen',
      GenerateDocument: 'Dokument generieren',
      AllDocumentsAnalyzed: 'Alle Dokumente analysiert',
      DocumentsInAnalysis: '{count} Dokumente noch in Analyse',
      DocumentsBeingAnalyzed:
        'Dokumente werden analysiert – die KI kann inkorrekte Ergebnisse produzieren',
      Processing: 'Verarbeitung:',
      DocumentsReady: 'Dokumente sind KI-bereit',
      Uploading: 'Wird hochgeladen:',
      DataExtraction: 'Datenextraktion',
      BasicAIAnalysis: 'Grundlegende KI-Analyse',
      Summary: 'Zusammenfassung',
      AdvancedAIAnalysis: 'Fortgeschrittene KI-Analyse',
      SummaryEmbedding: 'Zusammenfassungs-Embedding',
      Analysis: 'Analyse',
      Queued: 'In Warteschlange'
    }
  })

  // Inject case data and chat functionality
  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const sendChatMessage = inject(
    'sendChatMessage',
    async (session: AIChatSession | null, message: string) => {}
  )
  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const isActiveSessionGenerating = computed(() => {
    return activeChatSession.value && isSessionGenerating(activeChatSession.value.uuid)
  })
  const userChatMessage = ref('')
  const isStoreLoading = ref(true)
  // Use the case store for document tracking
  const caseStore = useCaseStore()

  // --- Ultra-Simple Document Processing Status ---
  // Direct computed properties for UI state
  const isProcessing = computed(() => {
    const result = caseStore.processingDocumentsCount > 0
    console.log('isProcessing computed:', result, 'count:', caseStore.processingDocumentsCount)
    return result
  })
  const hasCriticalErrors = computed(() => caseStore.documentsWithCriticalErrors.length > 0)
  const hasDocuments = computed(() => (caseStore.caseRecord?.documents?.length || 0) > 0)

  // Computed property for total documents processing (including uploads)
  const totalProcessingCount = computed(() => caseStore.processingDocumentsCount)

  // Success message handling
  const showSuccessMessage = ref(false)
  let successMessageTimeoutId: number | null = null

  // Add a flag to track previous processing state
  const wasPreviouslyProcessing = ref(false)

  // Initialize the previous state when component mounts
  onMounted(() => {
    // Set initial state after a brief delay to ensure store is loaded
    setTimeout(() => {
      wasPreviouslyProcessing.value = isProcessing.value
      console.log('Initial processing state:', isProcessing.value)
    }, 500)
  })

  // Watch for processing state changes
  watch(
    () => isProcessing.value,
    (isProcessingNow) => {
      console.log(
        'Processing state changed:',
        isProcessingNow,
        'previous:',
        wasPreviouslyProcessing.value
      )

      // If we just finished processing (was true, now false)
      if (
        wasPreviouslyProcessing.value &&
        !isProcessingNow &&
        hasDocuments.value &&
        !hasCriticalErrors.value
      ) {
        console.log('Documents finished processing successfully, showing success message')
        console.log(
          'hasDocuments:',
          hasDocuments.value,
          'hasCriticalErrors:',
          hasCriticalErrors.value
        )

        // Show success message
        showSuccessMessage.value = true

        // Auto-hide after 2 seconds
        if (successMessageTimeoutId) clearTimeout(successMessageTimeoutId)
        successMessageTimeoutId = window.setTimeout(() => {
          showSuccessMessage.value = false
          successMessageTimeoutId = null
        }, 2000)
      }

      // Update the previous state for next comparison
      wasPreviouslyProcessing.value = isProcessingNow
    }
  )

  // Reset success flag when new documents are added or processing starts
  watch(
    () => [caseStore.caseRecord?.documents?.length, caseStore.activeUploadsArray.length],
    () => {
      // If new documents are added or uploads start, hide success message
      showSuccessMessage.value = false
    }
  )

  // Also hide success message if processing starts again
  watch(
    () => isProcessing.value,
    (isProcessingNow) => {
      if (isProcessingNow) {
        showSuccessMessage.value = false
      }
    }
  )

  // Clean up on unmount
  onBeforeUnmount(() => {
    if (successMessageTimeoutId) clearTimeout(successMessageTimeoutId)
  })

  // ---------------
  // Chat functionality
  // ---------------

  const sendMessage = () => {
    if (userChatMessage.value.trim() && !isActiveSessionGenerating.value) {
      sendChatMessage(activeChatSession.value, userChatMessage.value.trim())
      userChatMessage.value = ''
    }
  }

  const isPromptPopoverOpen = inject<Ref<boolean>>('isPromptPopoverOpen', ref(false))

  const closePromptPopover = () => {
    isPromptPopoverOpen.value = false
  }

  // Handle prefill from template
  const prefillFromTemplate = (content: string) => {
    userChatMessage.value = content
  }

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }
</script>
<template>
  <!-- Message Input -->
  <div class="mt-auto border-t border-gray-200 bg-white px-4 pb-4">
    <!-- Document Analysis Status - Only one transition at the top level -->
    <transition
      name="fade"
      mode="out-in"
      :duration="{ enter: 300, leave: 500 }"
    >
      <div
        v-if="isProcessing || showSuccessMessage"
        key="status-container"
        class="mt-2 flex items-center gap-2"
      >
        <div
          :class="[
            'flex w-full items-center gap-1.5 rounded-md px-3 py-1 text-sm',
            isProcessing
              ? 'border border-purple-100 bg-purple-50 text-purple-700'
              : 'border border-green-100 bg-green-50 text-green-700'
          ]"
        >
          <!-- Status icon - no transition here -->
          <Loader2
            v-if="isProcessing"
            class="mr-1 size-4 shrink-0 animate-spin text-purple-500"
          />
          <CheckCircle
            v-else
            class="mr-1 size-4 shrink-0 text-green-500"
          />

          <span v-if="isProcessing">{{ t('DocumentsBeingAnalyzed') }}</span>
          <span v-else>{{ t('AllDocumentsAnalyzed') }}</span>

          <!-- Document processing details -->
          <div
            v-if="isProcessing"
            class="ml-auto flex items-center gap-1.5"
          >
            <div class="rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-800">
              {{ totalProcessingCount }}
              {{ totalProcessingCount === 1 ? 'Dokument' : 'Dokumente' }}
            </div>
          </div>

          <div
            v-else
            class="ml-auto rounded bg-green-100 px-2 py-0.5 text-xs text-green-800"
          >
            {{ t('DocumentsReady') }}
          </div>
        </div>
      </div>
    </transition>

    <div
      class="overflow-hidden rounded-lg border border-gray-200 shadow-sm"
      :class="isProcessing || showSuccessMessage ? 'mt-2' : 'mt-4'"
    >
      <textarea
        v-model="userChatMessage"
        :disabled="isActiveSessionGenerating"
        class="block max-h-36 w-full resize-none overflow-y-auto border-0 pb-2 pe-4 ps-2 pt-2 leading-4
          focus:outline-none focus:ring-0 disabled:pointer-events-none disabled:opacity-50 md:text-sm
          [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300
          [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar]:w-2"
        :placeholder="t('YourMessage')"
        style="height: 67px"
        @keydown="handleKeydown"
      ></textarea>

      <div class="mx-2 my-2 flex items-center justify-between gap-x-1">
        <!-- Button Group -->
        <div class="flex items-center gap-x-1">
          <!-- Button Group -->
          <div class="flex items-center gap-x-2">
            <!-- Prompt Templates Button with Popover -->
            <Popover v-model:open="isPromptPopoverOpen">
              <PopoverTrigger as-child>
                <button
                  type="button"
                  :disabled="isActiveSessionGenerating"
                  class="flex items-center justify-center gap-x-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-sm
                    font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500
                    focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                >
                  <Bookmark class="size-4 shrink-0" />
                  {{ t('PromptTemplates') }}
                </button>
              </PopoverTrigger>
              <PopoverContent class="p-0">
                <PromptTemplatesPopover
                  @close="closePromptPopover"
                  @prefill="prefillFromTemplate"
                />
              </PopoverContent>
            </Popover>
            <!-- Document Generate Button -->
            <a
              :href="`${ROUTES.APP.CASES.CASE_DETAIL.DOCUMENT_GENERATION.replace(':uuid', caseData.uuid)}`"
              class="flex items-center justify-center gap-x-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-sm
                font-medium text-gray-700 no-underline hover:bg-gray-50 focus:outline-none focus:ring-2
                focus:ring-purple-500 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              <FilePlus2 class="size-4 shrink-0" />
              {{ t('GenerateDocument') }}
            </a>
          </div>
          <!-- End Prompt Templates Button with Popover -->

          <!-- Button -->
          <button
            type="button"
            class="invisible flex size-8 items-center justify-center gap-x-1.5 rounded-full text-sm text-gray-600
              hover:bg-gray-100 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
          >
            <Paperclip class="size-4 shrink-0" />
            <span class="sr-only">{{ t('AttachFile') }}</span>
          </button>
          <!-- End Button -->
        </div>
        <!-- End Button Group -->

        <!-- Button Group -->
        <div class="flex items-center gap-x-1">
          <!-- Button -->
          <button
            type="button"
            class="invisible flex size-8 items-center justify-center gap-x-1.5 rounded-full text-sm text-gray-600
              hover:bg-gray-100 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
          >
            <Mic class="size-4 shrink-0" />
            <span class="sr-only">{{ t('RecordVoice') }}</span>
          </button>
          <!-- End Button -->

          <!-- Send Button -->
          <button
            type="button"
            :disabled="isActiveSessionGenerating || !userChatMessage.trim()"
            class="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-purple-600 text-sm font-medium
              text-white hover:bg-purple-700 focus:bg-purple-700 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
            @click="sendMessage"
          >
            <span
              v-if="isActiveSessionGenerating"
              class="animate-spin"
            >
              <Loader2 class="size-4" />
            </span>
            <span v-else>
              <SendHorizontal class="size-5 shrink-0" />
            </span>
            <span class="sr-only">
              {{ isActiveSessionGenerating ? t('Generating') : t('SendMessage') }}
            </span>
          </button>
          <!-- End Send Button -->
        </div>
        <!-- End Button Group -->
      </div>
    </div>
  </div>
  <!-- End Message Input -->
</template>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition:
      opacity 0.4s ease,
      transform 0.3s ease;
  }

  .fade-enter-from {
    opacity: 0;
    transform: translateY(-5px);
  }

  .fade-leave-to {
    opacity: 0;
    transform: translateY(5px);
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
